/**
 * Early session check — a second, tiny module entry loaded next to the app bundle (see index.html).
 *
 * A signed-in page cannot render before `GET /api/auth/session` answers (the roles decide which
 * route may render). That request used to start only once the ~250 kB app bundle had been
 * downloaded and run, i.e. strictly after it. This module is a few hundred bytes with no
 * dependencies, so it runs as soon as the HTML is parsed and fires the request while the bundle is
 * still downloading; the app picks the answer up with `takeEarlySession` (src/api/earlySession.ts)
 * instead of asking again.
 *
 * It must stay self-contained: importing anything else would drag that code into this entry. The
 * storage key and headers mirror src/api/client.ts (AUTH_TOKEN_KEY and the request interceptor).
 */
const AUTH_TOKEN_KEY = 'auth_token';

try {
  const token = sessionStorage.getItem(AUTH_TOKEN_KEY) || localStorage.getItem(AUTH_TOKEN_KEY);
  if (token) {
    // Same origin rule as the generated client: an absolute VITE_API_BASE_URL keeps its origin,
    // a relative one (production, behind the reverse proxy) resolves against the page.
    let origin = '';
    try {
      origin = new URL(import.meta.env.VITE_API_BASE_URL || '/api').origin;
      if (origin === window.location.origin) origin = '';
    } catch {
      origin = '';
    }

    const language = (localStorage.getItem('i18nextLng') || 'en').split('-')[0] ?? 'en';
    const request = fetch(`${origin}/api/auth/session`, {
      headers: { Authorization: `Bearer ${token}`, 'Accept-Language': language },
    }).then(async (response) => {
      let data: unknown = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }
      // Shaped like axios' resolved / rejected values, which is what AuthContext already handles.
      if (!response.ok) throw { response: { status: response.status, data } };
      return { data };
    });
    // The app may never ask (e.g. navigation elsewhere first): do not report an unhandled rejection.
    request.catch(() => undefined);
    (window as unknown as { __earlySession?: Promise<{ data: unknown }> }).__earlySession = request;
  }
} catch {
  // Storage or fetch unavailable: the app falls back to its normal session request.
}
